import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Handshake, ExternalLink, Network, Building2, BadgeCheck, Users } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

const KIND_LABEL = {
  rete_stabile: "Reti stabili",
  interlocutore_istituzionale: "Interlocutori istituzionali",
  patrocinio: "Patrocinî",
  partner_progetto: "Partner di progetto",
  collaborazione_occasionale: "Collaborazioni occasionali",
};
const KIND_INTRO = {
  rete_stabile: "Realtà con cui portiamo avanti nel tempo percorsi condivisi.",
  interlocutore_istituzionale: "Istituzioni con cui manteniamo un dialogo costante.",
  patrocinio: "Enti che riconoscono e sostengono formalmente i nostri progetti.",
  partner_progetto: "Organizzazioni che hanno costruito con noi progetti specifici.",
  collaborazione_occasionale: "Realtà con cui abbiamo condiviso singoli eventi o iniziative.",
};
const KIND_ICON = {
  rete_stabile: Network,
  interlocutore_istituzionale: Building2,
  patrocinio: BadgeCheck,
  partner_progetto: Users,
  collaborazione_occasionale: Handshake,
};

export default function RetiCollaborazioni() {
  const [partners, setPartners] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tab, setTab] = useState("attive");

  useEffect(() => {
    (async () => {
      const [p, pr] = await Promise.all([api.get("/collaborazioni"), api.get("/projects")]);
      setPartners(p.data); setProjects(pr.data);
    })();
  }, []);

  const projectMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  const filtered = partners.filter((p) => (tab === "attive" ? p.status !== "conclusa" : p.status === "conclusa"));
  const grouped = useMemo(() => {
    const g = {};
    for (const p of filtered) {
      const k = p.kind || "collaborazione_occasionale";
      (g[k] = g[k] || []).push(p);
    }
    return g;
  }, [filtered]);

  const kindOrder = ["rete_stabile", "interlocutore_istituzionale", "patrocinio", "partner_progetto", "collaborazione_occasionale"];

  return (
    <div data-testid="reti-page" className="min-h-screen">
      {/* Hero */}
      <section className="bg-[#FFF1E8] py-14 lg:py-20">
        <div className="container-vv">
          <div className="eyebrow">Non lo facciamo da soli</div>
          <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl tracking-tight text-[#292522]">Reti e collaborazioni</h1>
          <p className="mt-6 text-lg leading-relaxed text-[#292522]/80 max-w-3xl">
            Vola Vola cresce insieme ad altre realtà del territorio: reti stabili, istituzioni, partner di progetto, collaborazioni occasionali. Qui trovi chi cammina con noi oggi — e chi ha camminato con noi negli anni.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="container-vv pt-10">
        <div className="inline-flex bg-[#F8F1E8] rounded-full p-1" role="tablist">
          <button onClick={() => setTab("attive")} role="tab" aria-selected={tab === "attive"} data-testid="tab-attive"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "attive" ? "bg-[#FF6100] text-white" : "text-[#292522]/70"}`}>
            Collaborazioni attive
          </button>
          <button onClick={() => setTab("concluse")} role="tab" aria-selected={tab === "concluse"} data-testid="tab-concluse"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "concluse" ? "bg-[#FF6100] text-white" : "text-[#292522]/70"}`}>
            Storico
          </button>
        </div>
      </section>

      {/* Groups */}
      <section className="container-vv py-10 space-y-14">
        {kindOrder.filter((k) => grouped[k]?.length).map((k) => {
          const Icon = KIND_ICON[k] || Handshake;
          return (
            <div key={k} data-testid={`group-${k}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-[#FF6100]/10 text-[#FF6100] flex items-center justify-center">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-medium">{KIND_LABEL[k]}</h2>
                  <p className="text-sm text-[#292522]/60">{KIND_INTRO[k]}</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[k].map((p) => {
                  const linked = (p.linked_projects || []).map((id) => projectMap[id]).filter(Boolean);
                  return (
                    <article key={p.id} className="card-vv p-5 bg-white flex flex-col gap-3" data-testid={`partner-${p.name}`}>
                      <div className="flex items-start gap-3">
                        {p.logo ? (
                          <img src={fileUrl(p.logo)} alt={p.name} className="h-14 w-14 object-contain bg-[#FFF1E8] rounded-xl p-1.5" />
                        ) : (
                          <div className="h-14 w-14 rounded-xl bg-[#FFF1E8] flex items-center justify-center text-[#FF6100]"><Handshake className="h-6 w-6" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold leading-tight">{p.name}</h3>
                          {p.year_started && (
                            <div className="text-xs text-[#292522]/60 mt-1">
                              {p.year_started}{p.year_ended && p.year_ended !== p.year_started ? `–${p.year_ended}` : (p.status === "conclusa" ? "" : " · in corso")}
                            </div>
                          )}
                        </div>
                      </div>
                      {p.description && <p className="text-sm text-[#292522]/75 leading-relaxed line-clamp-4">{p.description}</p>}
                      {linked.length > 0 && (
                        <div className="pt-3 border-t border-[#292522]/8">
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#292522]/50 mb-2">Progetti collegati</div>
                          <div className="flex flex-wrap gap-1.5">
                            {linked.map((pr) => (
                              <Link key={pr.id} to={`/progetti/${pr.slug}`} className="inline-flex text-xs px-2.5 py-1 rounded-full bg-[#FFF1E8] text-[#FF6100] hover:bg-[#FF6100] hover:text-white transition-colors">
                                {pr.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                      {p.url && (
                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center gap-1 text-xs text-[#FF6100] hover:underline">
                          Visita il sito <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          );
        })}
        {Object.keys(grouped).length === 0 && (
          <div className="rounded-3xl bg-[#F8F1E8] p-12 text-center text-[#292522]/60">
            {tab === "attive" ? "Al momento nessuna collaborazione attiva pubblicata." : "Archivio storico in aggiornamento."}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="container-vv pb-16">
        <div className="rounded-3xl bg-[#292522] text-[#FFFCF8] p-8 lg:p-12">
          <div className="eyebrow text-[#FF6100]/90">Vuoi camminare con noi?</div>
          <h2 className="mt-3 font-display font-medium text-3xl sm:text-4xl">Facciamo qualcosa insieme</h2>
          <p className="mt-3 text-[#FFFCF8]/75 max-w-2xl">Sei un'associazione, un ente, una realtà del territorio? Scrivici: cerchiamo alleanze, non semplici endorsement.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contatti" className="btn-primary text-sm">Contattaci</Link>
            <Link to="/partecipa" className="btn-ghost text-sm text-[#FFFCF8] border-[#FFFCF8]/30">Come partecipare</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
